// Is a chart label a machine-readable PERIOD KEY, and how does it read on a screen?
//
// The engine deliberately emits period keys, not display text: `2026-03` for a month, `2026-Q1`
// for a quarter, `2026` for a year. That is the right call — the key sorts correctly and every
// programmatic consumer reads it — but the product's own dashboards render a month as an
// ABBREVIATED month name at 4 of 4 axis sites, and its AI page formats the axis at render for
// exactly that reason. This page showed the raw key, so a monthly trend (most demo charts) read
// `2026-03` beside a product screen reading `Mar 2026`.
//
// ★ THIS FILE IS DELIBERATELY NARROW, AND THAT IS THE WHOLE POINT.
//   The same axis carries CATEGORY labels — a site name, a status, a template title — and a
//   category is the one thing here that must never be rewritten. A site legitimately named
//   "2026-03" is a legal category label, so the test is an ANCHORED `^\d{4}-\d{2}$` plus a real
//   calendar-month check: `2026-13` and `2026-00` are not months and are left alone. Anything
//   that is not a month key passes through byte-identical, the quarter and year keys included —
//   a year already reads the way the product renders it, and the quarter has no product format
//   to copy.
//
// ★ IT IS NOT THE TABLE'S DATE FORMAT AND MUST NOT BECOME IT. `resultDates.mjs` formats a DATE
//   COLUMN in the result table, keyed on the engine's `field` enum; this formats a period KEY
//   used as a chart label. A chart axis is not a date in a table, and the two have separate
//   vocabularies on purpose.
//
// ★ AND THE MONTH NAME IS AN ENGLISH LITERAL, on the same terms `resultDates.mjs` states. The
//   product renders through `datePickerReadableFormat` (`MMM YYYY` via `_tDate`), which needs
//   `moment` and the i18n `common` namespace; this client has `dayjs` and no i18n at all. The
//   two agree byte-for-byte on all twelve English months — measured, in four zones — so this is
//   the same text by a different route, not an approximation. A future i18n pass owns it.
//
// Unlike the product's copy this file needs no injected formatter: `dayjs` loads under node, so
// the offline gate drives the real month text rather than a spy.

// `dayjs` only — no `utc` plugin, and that is deliberate. The month is built as a LOCAL date at
// local midnight and formatted locally, so no zone boundary is ever crossed and there is nothing
// for a negative offset to shift. (Were a plugin needed it would have to be imported as
// `dayjs/plugin/utc.js` WITH the extension: the conventional extensionless form compiles under
// the CRA build and is unloadable under node's ESM resolver, which is what the gate runs on.)
import dayjs from "dayjs";

// Anchored on purpose. `^` and `$` are what stop "2026-03 Site" and "RA-2026-03" from being
// treated as a period.
export const MONTH_KEY_RE = /^(\d{4})-(\d{2})$/;

// The {year, month} a month key names, or null when the string is not one. `month` is 1-based,
// as written. An impossible month is rejected, so a category that merely LOOKS like a period
// ("2026-13") is not one.
export function monthKeyParts(label) {
  if (typeof label !== "string") return null;
  const m = MONTH_KEY_RE.exec(label);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!(month >= 1 && month <= 12)) return null;
  return { year, month };
}

// A month key rendered for display; every other label returned unchanged, and unchanged means
// the same value, not a stringified copy — a numeric or null label reaches the renderer as it
// was.
export function formatPeriodLabel(label) {
  const parts = monthKeyParts(label);
  if (!parts) return label;
  const d = dayjs(new Date(parts.year, parts.month - 1, 1));
  return d.isValid() ? d.format("MMM YYYY") : label;
}

export default formatPeriodLabel;
